import { useState, useMemo } from 'react';
import { ParsedRow } from './csvUtils';

interface EdicaoMassaStepProps {
  rows: ParsedRow[];
  onConfirm: (rows: ParsedRow[]) => void;
  onBack: () => void;
}

const CARREIRAS = ['Técnica Superior', 'Assistente Técnico', 'Assistente Operacional'];
const AVALIACOES = ['Excelente', 'Muito Bom', 'Adequado', 'Bom', 'Regular', 'Inadequado', 'Relevante'];
const SEXOS = ['M', 'F'];

// Campos editáveis em massa
type EditableField = 'carreira' | 'nivel_atual' | 'novo_nivel_2025' | 'vencimento' | 'avaliacao' | 'sexo' | 'unidade_organica' | 'status';

const FIELD_CONFIG: { field: EditableField; label: string; type: 'select' | 'number' | 'text'; options?: string[] }[] = [
  { field: 'carreira', label: 'Carreira', type: 'select', options: CARREIRAS },
  { field: 'nivel_atual', label: 'Nível Atual', type: 'number' },
  { field: 'novo_nivel_2025', label: 'Novo Nível 2025', type: 'number' },
  { field: 'vencimento', label: 'Vencimento (€)', type: 'number' },
  { field: 'avaliacao', label: 'Avaliação', type: 'select', options: AVALIACOES },
  { field: 'sexo', label: 'Sexo', type: 'select', options: SEXOS },
  { field: 'unidade_organica', label: 'Unidade Orgânica', type: 'text' },
  { field: 'status', label: 'Estado', type: 'text' },
];

export default function EdicaoMassaStep({ rows, onConfirm, onBack }: EdicaoMassaStepProps) {
  const [editedRows, setEditedRows] = useState<ParsedRow[]>(rows.map((r) => ({ ...r })));
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [bulkField, setBulkField] = useState<EditableField>('carreira');
  const [bulkValue, setBulkValue] = useState('');
  const [filterMissing, setFilterMissing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredRows = useMemo(() => {
    return editedRows.filter((r) => {
      const matchSearch = !searchTerm || r.nome.toLowerCase().includes(searchTerm.toLowerCase()) || r.id.toLowerCase().includes(searchTerm.toLowerCase());
      const matchMissing = !filterMissing || !r.carreira || r.vencimento === 0 || r.nivel_atual === 0;
      return matchSearch && matchMissing;
    });
  }, [editedRows, searchTerm, filterMissing]);

  const rowsMissingData = useMemo(
    () => editedRows.filter((r) => !r.carreira || r.vencimento === 0 || r.nivel_atual === 0).length,
    [editedRows],
  );

  const toggleSelect = (rowIndex: number) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) next.delete(rowIndex);
      else next.add(rowIndex);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedRows.size === filteredRows.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(filteredRows.map((r) => r._rowIndex)));
    }
  };

  const applyBulk = () => {
    if (!bulkValue.trim() && bulkValue !== '0') return;
    const targetIndices = selectedRows.size > 0
      ? selectedRows
      : new Set(filteredRows.map((r) => r._rowIndex));

    setEditedRows((prev) => prev.map((r) => {
      if (!targetIndices.has(r._rowIndex)) return r;
      const updated = { ...r };
      if (bulkField === 'nivel_atual' || bulkField === 'novo_nivel_2025') {
        (updated as Record<string, unknown>)[bulkField] = parseInt(bulkValue, 10) || 0;
      } else if (bulkField === 'vencimento') {
        updated.vencimento = parseFloat(bulkValue.replace(',', '.')) || 0;
      } else {
        (updated as Record<string, unknown>)[bulkField] = bulkValue;
      }
      return updated;
    }));
    setSelectedRows(new Set());
  };

  const updateCell = (rowIndex: number, field: EditableField, value: string) => {
    setEditedRows((prev) => prev.map((r) => {
      if (r._rowIndex !== rowIndex) return r;
      const updated = { ...r };
      if (field === 'nivel_atual' || field === 'novo_nivel_2025') {
        (updated as Record<string, unknown>)[field] = parseInt(value, 10) || 0;
      } else if (field === 'vencimento') {
        updated.vencimento = parseFloat(value.replace(',', '.')) || 0;
      } else {
        (updated as Record<string, unknown>)[field] = value;
      }
      return updated;
    }));
  };

  const bulkConfig = FIELD_CONFIG.find((f) => f.field === bulkField);

  return (
    <div className="p-6 space-y-4">
      {/* Cabeçalho */}
      <div className="flex items-start gap-3 p-4 bg-zinc-50 border border-zinc-100 rounded-xl">
        <i className="ri-edit-2-line text-zinc-500 text-base flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-[13px] font-semibold text-zinc-800">Edição em massa</p>
          <p className="text-[12px] text-zinc-500 mt-0.5">
            Corrige rapidamente campos em falta antes de importar.
            {rowsMissingData > 0 && (
              <span className="ml-1 text-amber-600 font-semibold">{rowsMissingData} funcionário(s) com dados incompletos.</span>
            )}
          </p>
        </div>
      </div>

      {/* Barra de ação em massa */}
      <div className="bg-zinc-900 rounded-xl p-3 flex items-center gap-3 flex-wrap">
        <span className="text-[11px] text-zinc-400 font-semibold uppercase tracking-widest whitespace-nowrap">Aplicar em massa</span>
        <select
          value={bulkField}
          onChange={(e) => { setBulkField(e.target.value as EditableField); setBulkValue(''); }}
          className="text-[12px] bg-zinc-800 text-white border border-zinc-700 rounded-lg px-2.5 py-1.5 cursor-pointer outline-none"
        >
          {FIELD_CONFIG.map((f) => (
            <option key={f.field} value={f.field}>{f.label}</option>
          ))}
        </select>
        {bulkConfig?.type === 'select' ? (
          <select
            value={bulkValue}
            onChange={(e) => setBulkValue(e.target.value)}
            className="text-[12px] bg-zinc-800 text-white border border-zinc-700 rounded-lg px-2.5 py-1.5 cursor-pointer outline-none flex-1 min-w-32"
          >
            <option value="">— Escolher —</option>
            {bulkConfig.options?.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        ) : (
          <input
            type={bulkConfig?.type === 'number' ? 'number' : 'text'}
            value={bulkValue}
            onChange={(e) => setBulkValue(e.target.value)}
            placeholder={`Valor para ${bulkConfig?.label}`}
            className="text-[12px] bg-zinc-800 text-white border border-zinc-700 rounded-lg px-2.5 py-1.5 outline-none flex-1 min-w-32 placeholder-zinc-500"
          />
        )}
        <button
          onClick={applyBulk}
          disabled={!bulkValue.trim()}
          className="whitespace-nowrap flex items-center gap-1.5 px-3 py-1.5 bg-white text-zinc-900 text-[12px] font-semibold rounded-lg hover:bg-zinc-100 transition-colors cursor-pointer disabled:opacity-40"
        >
          <i className="ri-check-line text-sm" />
          {selectedRows.size > 0 ? `Aplicar a ${selectedRows.size} selecionados` : 'Aplicar a todos visíveis'}
        </button>
      </div>

      {/* Filtros */}
      <div className="flex items-center gap-3">
        <div className="flex-1 relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Pesquisar por nome ou nº..."
            className="w-full text-[12px] border border-zinc-200 rounded-lg pl-8 pr-3 py-2 outline-none focus:border-zinc-400 transition-colors"
          />
        </div>
        <button
          onClick={() => setFilterMissing((v) => !v)}
          className={`whitespace-nowrap flex items-center gap-2 text-[12px] font-medium px-3 py-2 rounded-lg border transition-colors cursor-pointer ${
            filterMissing
              ? 'bg-amber-50 border-amber-300 text-amber-700'
              : 'bg-white border-zinc-200 text-zinc-500 hover:border-zinc-300'
          }`}
        >
          <i className="ri-filter-3-line text-sm" />
          Só incompletos {rowsMissingData > 0 && `(${rowsMissingData})`}
        </button>
        <span className="text-[12px] text-zinc-400">{filteredRows.length} de {editedRows.length}</span>
      </div>

      {/* Tabela editável */}
      <div className="border border-zinc-100 rounded-xl overflow-hidden">
        <div className="overflow-x-auto max-h-72 overflow-y-auto">
          <table className="w-full text-[11px]">
            <thead className="sticky top-0 z-10">
              <tr className="bg-zinc-50 border-b border-zinc-100">
                <th className="px-3 py-2.5 text-left">
                  <input
                    type="checkbox"
                    checked={selectedRows.size === filteredRows.length && filteredRows.length > 0}
                    onChange={toggleSelectAll}
                    className="cursor-pointer"
                  />
                </th>
                <th className="px-3 py-2.5 text-left font-semibold text-zinc-400 whitespace-nowrap">Nº</th>
                <th className="px-3 py-2.5 text-left font-semibold text-zinc-400 whitespace-nowrap">Nome</th>
                <th className="px-3 py-2.5 text-left font-semibold text-zinc-400 whitespace-nowrap">Carreira</th>
                <th className="px-3 py-2.5 text-left font-semibold text-zinc-400 whitespace-nowrap">Nível</th>
                <th className="px-3 py-2.5 text-left font-semibold text-zinc-400 whitespace-nowrap">Vencimento</th>
                <th className="px-3 py-2.5 text-left font-semibold text-zinc-400 whitespace-nowrap">Avaliação</th>
                <th className="px-3 py-2.5 text-left font-semibold text-zinc-400 whitespace-nowrap">Sexo</th>
                <th className="px-3 py-2.5 text-left font-semibold text-zinc-400 whitespace-nowrap">Unidade</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row) => {
                const isSelected = selectedRows.has(row._rowIndex);
                const isMissing = !row.carreira || row.vencimento === 0 || row.nivel_atual === 0;
                return (
                  <tr
                    key={row._rowIndex}
                    className={`border-b border-zinc-50 transition-colors ${isSelected ? 'bg-zinc-50' : isMissing ? 'bg-amber-50/30' : 'hover:bg-zinc-50/50'}`}
                  >
                    <td className="px-3 py-1.5">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(row._rowIndex)}
                        className="cursor-pointer"
                      />
                    </td>
                    <td className="px-3 py-1.5 font-mono text-zinc-400 whitespace-nowrap">{row.id}</td>
                    <td className="px-3 py-1.5 font-medium text-zinc-700 whitespace-nowrap max-w-32 truncate">{row.nome}</td>
                    <td className="px-3 py-1.5">
                      <select
                        value={row.carreira}
                        onChange={(e) => updateCell(row._rowIndex, 'carreira', e.target.value)}
                        className={`text-[11px] border rounded-md px-1.5 py-0.5 cursor-pointer outline-none w-full ${
                          !row.carreira ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-zinc-200 bg-white text-zinc-700'
                        }`}
                      >
                        {CARREIRAS.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-1.5">
                      <input
                        type="number"
                        value={row.nivel_atual}
                        onChange={(e) => updateCell(row._rowIndex, 'nivel_atual', e.target.value)}
                        className={`text-[11px] border rounded-md px-1.5 py-0.5 outline-none w-14 ${
                          row.nivel_atual === 0 ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-zinc-200 bg-white text-zinc-700'
                        }`}
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          value={row.vencimento}
                          onChange={(e) => updateCell(row._rowIndex, 'vencimento', e.target.value)}
                          className={`text-[11px] border rounded-md px-1.5 py-0.5 outline-none w-20 ${
                            row.vencimento === 0 ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-zinc-200 bg-white text-zinc-700'
                          }`}
                        />
                        <span className="text-zinc-400">€</span>
                      </div>
                    </td>
                    <td className="px-3 py-1.5">
                      <select
                        value={row.avaliacao}
                        onChange={(e) => updateCell(row._rowIndex, 'avaliacao', e.target.value)}
                        className="text-[11px] border border-zinc-200 rounded-md px-1.5 py-0.5 cursor-pointer outline-none bg-white text-zinc-700 w-full"
                      >
                        {AVALIACOES.map((a) => <option key={a} value={a}>{a}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-1.5">
                      <select
                        value={row.sexo}
                        onChange={(e) => updateCell(row._rowIndex, 'sexo', e.target.value)}
                        className="text-[11px] border border-zinc-200 rounded-md px-1.5 py-0.5 cursor-pointer outline-none bg-white text-zinc-700"
                      >
                        {SEXOS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-1.5">
                      <input
                        type="text"
                        value={row.unidade_organica}
                        onChange={(e) => updateCell(row._rowIndex, 'unidade_organica', e.target.value)}
                        className="text-[11px] border border-zinc-200 rounded-md px-1.5 py-0.5 outline-none bg-white text-zinc-700 w-28"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Rodapé */}
      <div className="flex items-center justify-between pt-1">
        <button
          onClick={onBack}
          className="whitespace-nowrap flex items-center gap-2 text-[13px] text-zinc-500 hover:text-zinc-800 border border-zinc-200 px-4 py-2 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line text-sm" />
          Voltar
        </button>
        <div className="flex items-center gap-3">
          {rowsMissingData > 0 && (
            <span className="text-[12px] text-amber-600 flex items-center gap-1">
              <i className="ri-alert-line text-sm" />
              {rowsMissingData} com dados incompletos
            </span>
          )}
          <button
            onClick={() => onConfirm(editedRows)}
            className="whitespace-nowrap flex items-center gap-2 px-5 py-2 bg-zinc-900 text-white text-[13px] font-medium rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
          >
            <i className="ri-upload-2-line text-sm" />
            Importar {editedRows.length} funcionário(s)
          </button>
        </div>
      </div>
    </div>
  );
}
